
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { UrlResolver } from '../../modules/urlResolver';
import { KryptonElement } from '../krypton-element';
import '../loading-curtain';
import { isIE11, localizeScript, scripter } from "../../modules/scripter";
import { styler } from "../../modules/styler";
import { KryptonConstants } from "../../modules/krypton_constants";
import { getVersion } from "../../modules/global_function";

const deCacheToken = '&deCacheToken=';
const webDispatch = '/WebDispatch/';
// Landing page widget content loader implementation.  Whenever the URL property is changed, the content will be reloaded from the new URL.
export class LandingPageContentLoader extends KryptonElement {
    private _url: string = '';
    private _widgetId: string = '';
    private _responseText: string = '';
    private isFirstLoad = true;
    private _scriptElements: HTMLScriptElement[] = [];
    private _styleElements: HTMLStyleElement[] = [];


    // Begin getters and setters for properties
    get url(): string {
        return this._url;
    }

    @property({ type: String })
    set url(value: string) {
        const oldValue = this._url;
        this._url = value;
        this.requestUpdate('url', oldValue);

        this.showLoadingCurtain();
        // URL is updated, so trigger a reload
        // Let the page come back fast, the widgets may continue to load later
        setTimeout(() => { this.reload(); }, this.isFirstLoad ? 1000 : 0);
    }

    get widgetId(): string {
        return this._widgetId;
    }

    @property({ type: String })
    set widgetId(value: string) {
        const oldValue = this._widgetId;
        this._widgetId = value;
        this.requestUpdate('widgetId', oldValue);
    }
    // End getters and setters for properties

    getLoadingCurtain() {
        return this.shadowRoot?.querySelector("loading-curtain");
    }

    showLoadingCurtain() {
        if (this.isFirstLoad) return;
        let lc = this.getLoadingCurtain();
        if (lc) {
            lc.classList.add("short");
            lc.parentElement?.classList.add("form-floating");
            lc.opened = true;
        }
    }

    hideLoadingCurtain() {
        this.isFirstLoad = false;
        let lc = this.getLoadingCurtain();
        if (lc) {
            lc.classList.remove("short");
            lc.parentElement?.classList.remove("form-floating");
            lc.opened = false;
        }
    }

    // A place holder.  We need this content to properly reserve the space for the content in the widget
    render() {
        return html`
            <link rel="stylesheet" href="/resources/styles/krypton-nextgen.css">
            <div>
                <div id="content_loader_container" data-widget-id=${this.widgetId}><img src="/images/loading.gif" style="padding-left:46%"></img></div>
                <loading-curtain id="lp-loading-curtain" ?opened=${false}></loading-curtain>
            </div>
        `;
    }

    // Fetch data from the current URL and display as the content
    async reload() {
        this.showLoadingCurtain();
        // Get the formatted URL (add a query string to avoid cache) and fetch the result
        const response = await fetch(this.getFormattedUrl()),
            responseText = await response.text();

        // Create a new div element and add the content to it
        const container = this.shadowRoot?.querySelector('#content_loader_container') as any;
        if (container) {
            container.textContent = "";
            const fragment = this.getContentHtml(responseText);
            this.initializeScripts(fragment);
            this.initializeStyles(fragment);
            for (let c of [...fragment.childNodes]) {
                container.appendChild(c);
            }
            await this.applyScriptsAndStyles(container);
            // container.appendChild(this.getContentHtml(responseText))
            this.hideLoadingCurtain();
        } else {
            this._responseText = responseText;
            let that = this;
            setTimeout(() => { that.loadContent(); }, 1000);
        }

        // Div is ready to be shown now, remove all children now and immediately add the new div to avoid flickering
        //this.childNodes.forEach(e => e.remove());
        //this.appendChild(fragment);
    }

    async loadContent() {
        console.log("Retrying to set response text to content loader..")
        const container = this.shadowRoot?.querySelector('#content_loader_container') as any;
        if (container) {
            container.textContent = "";
            const fragment = this.getContentHtml(this._responseText);
            this.initializeScripts(fragment);
            this.initializeStyles(fragment);
            for (let c of [...fragment.childNodes]) {
                container.appendChild(c);
            }
            await this.applyScriptsAndStyles(container);
            // container.appendChild(this.getContentHtml(this._responseText));
        } else {
            let that = this;
            setTimeout(() => { that.loadContent(); }, 1000);
        }
    }

    initializeScripts(source: HTMLElement) {
        // By default set onRefreshDocument to openModule to handle scenarios in which page doesn't define onRefreshDocument.  If page defines it,
        // this will be overridden when the scripts are applied
        for (let s of source.querySelectorAll("script")) {

            let script: HTMLScriptElement | void;

            if (s.hasAttribute("noscript")) {
                s.parentElement?.removeChild(s);
                continue;
            }

            if (isIE11 && s.getAttribute("type") == "module") { //TODO import statements will not work
                if (script = localizeScript(s)) {
                    this._scriptElements.push(script);
                    s.parentElement?.removeChild(s);
                    continue;
                }
            }
            if (s.hasAttribute("src")) {
                this._scriptElements.push(s);
                s.parentElement?.removeChild(s);
                continue;
            }
            if (script = scripter(s)) {
                this._scriptElements.push(script);
                s.parentElement?.removeChild(s);
                continue;
            }
        }
    }

    initializeStyles(source: HTMLElement) {
        const scopedId = source.querySelector("main")?.getAttribute("id") as string;
        for (let s of source.querySelectorAll("style")) {
            s.parentElement?.removeChild(s);
            let css: HTMLStyleElement | void;
            css = styler(s, scopedId);
            if (css) {
                this._styleElements.push(css);
            } else {
                this._styleElements.push(s);
            }
        }
    }

    async applyScriptsAndStyles(container: any) {
        // Let's apply styles before scripts to avoid flickering
        this._styleElements.forEach(s => {
            container.appendChild(s);
        });
        let promiseArray = [] as Promise<any>[];
        for (const s of this._scriptElements) {
            if (s.hasAttribute("src")) {
                if (s.getAttribute("src")) {
                    promiseArray.push(this.loadJavaScript(s.getAttribute("src")));
                    container.appendChild(s);
                }
            } else {
                await Promise.all(promiseArray);
                promiseArray = [];
                var e = document.createElement("script");
                var inlineScript = document.createTextNode(s.text);
                e.appendChild(inlineScript);
                container.appendChild(e);
            }
        }
        //wait for any remaining scripts
        await Promise.all(promiseArray);

    }

    async loadJavaScript(url: any) {
        let versionedUrl = url + "?v=" + getVersion();
        let promise = new Promise((resolve, reject) => {
            window["jQuery"].ajax({
                url: versionedUrl,
                dataType: 'script',
                cache: true,
                success: () => { resolve(true); },
                error: () => { reject('Ajax request failed'); }
            });
        });
        return promise;
    }


    // Returns a random number.  This is used to avoid retrieving the content from cache.
    getRandomNum() {
        return Math.floor(Math.random() * 5000);
    }

    //Parse the url, remove decache token, then add it with new random number
    getFormattedUrl() {
        var curUrl = this._url;

        // Remove Webdispatch for now, will be added later
        if (curUrl && curUrl.startsWith(webDispatch)) {
            curUrl = curUrl.substr(13);
        }

        // Remove deCacheToken for now, will be added later
        var pos = curUrl.indexOf(deCacheToken);
        if (pos > -1) {
            curUrl = curUrl.substr(0, pos);
        }
        curUrl = UrlResolver.RELATIVE.resolve(curUrl);
        if (curUrl.indexOf('widgetId') < 0) {
            curUrl = curUrl + '&widgetId=' + this.widgetId;
        }

        // Construct the full URL with Webdispatch and deCacheToken
        curUrl = curUrl + deCacheToken + this.getRandomNum();

        return curUrl;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'landing-page-content-loader': LandingPageContentLoader;
    }
}
